import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './EventView.module.css';
import { eventApi, EventViewData, User, registerUserForEvent, unregisterUserFromEvent, 
  getTimeTable, 
  updateTimeTable, 
  addTimeTableEntry, 
  updateTimeTableEntry, 
  deleteTimeTableEntry,
  TimeEntry, 
  TimeTableResponse 
} from '../../../services/eventService';
import {
  ActionMenuModal,
  RegisteredUsersModal,
  RegisterUserModal,
  SendMailModal,
  EmailResultModal,
  VotesModal,
  TimeTableModal,
  TimelineModal,
  UnmatchedUsersModal,
  AddUsersModal,
  EditEventModal
} from './components/Modals';
import GroupMatches from './components/GroupMatches';
import {
  getErrorMessage,
  getRegisteredUsers,
  getUnregisteredUsers,
  getFilteredUnregisteredUsers,
  handleSendVotingEmail,
  handleDownloadPlan,
  handleDownloadExcel,
  handleDownloadVotes,
  handleGenerateMatches,
  handleRegisterUser,
  handleCopyVerificationCode,
  handleCopyRegisterLink,
  handleCopyVoteLink,
  handleCopySpotlightLink
} from './utils';

const EventView: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState<EventViewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showRegisteredUsersModal, setShowRegisteredUsersModal] = useState(false);
  const [showRegisterUserModal, setShowRegisterUserModal] = useState(false);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [showVotesModal, setShowVotesModal] = useState(false);
  const [showTimeTableModal, setShowTimeTableModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showEmailResultModal, setShowEmailResultModal] = useState(false);
  const [emailResult, setEmailResult] = useState<any>(null);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [selectedServer, setSelectedServer] = useState('');
  const [showUnmatchedUsersModal, setShowUnmatchedUsersModal] = useState(false);
  const [unmatchedUsers, setUnmatchedUsers] = useState<User[]>([]);
  const [selectedRoundForUnmatched, setSelectedRoundForUnmatched] = useState<number | null>(null);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [selectedGroupForAddUsers, setSelectedGroupForAddUsers] = useState<{roundNumber: number, groupId: string} | null>(null);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<string[]>([]);
  const [draggedUser, setDraggedUser] = useState<User | null>(null);
  const [timeTableCount, setTimeTableCount] = useState<number>(0);
  const isMountedRef = useRef<boolean>(true);

  // Load initial timetable count
  const loadInitialTimeTableCount = async () => {
    if (!eventId) return;
    
    try {
      const response = await getTimeTable(eventId);
      setTimeTableCount(response.entries.length);
    } catch (error) {
      console.error('Error loading initial timetable count:', error);
      // Don't show error toast for this since it's optional data
    }
  };

  // Callback function for timetable updates
  const handleTimeTableUpdate = (count: number) => {
    setTimeTableCount(count);
  };

  // Fetch event view data
  useEffect(() => {
    let isComponentMounted = true;

    const fetchEventData = async () => {
      if (!eventId) {
        toast.error('Event ID is required', {
          position: "top-right",
          autoClose: 5000
        });
        navigate('/events');
        return;
      }

      try {
        setLoading(true);
        const data = await eventApi.getEventViewData(eventId);
        
        if (isComponentMounted) {
          setEventData(data);
          setLoading(false);
          // Load initial timetable count
          loadInitialTimeTableCount();
        }
      } catch (error) {
        console.error('Error fetching event data:', error);
        const errorMessage = await getErrorMessage(error);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000
        });
        
        if (isComponentMounted) {
          setLoading(false);
        }
      }
    };

    fetchEventData();

    return () => {
      isComponentMounted = false;
      isMountedRef.current = false;
    };
  }, [eventId, navigate]);

  // Send voting email
  const onSendVotingEmail = async () => {
    if (!eventData?.event.Events_ID) return;

    try {
      const result = await handleSendVotingEmail(eventData.event.Events_ID);
      setEmailResult(result);
      setShowEmailResultModal(true);
      setShowSendMailModal(false);
    } catch (error) {
      console.error('Error sending voting emails:', error);
    }
  };

  // Copy verification code
  const onCopyVerificationCode = () => {
    if (eventData?.event.Verification_Code) {
      handleCopyVerificationCode(eventData.event.Verification_Code);
    }
  };

  // Copy register link
  const onCopyRegisterLink = () => {
    if (!eventData?.event.Events_ID || !eventData?.event.Verification_Code) return;
    
    const baseUrl = selectedServer || window.location.origin;
    handleCopyRegisterLink(eventData.event.Events_ID, eventData.event.Verification_Code, baseUrl);
  };

  // Copy vote link (participants link)
  const onCopyVoteLink = () => {
    if (!eventData?.event.Events_ID) return;
    
    const baseUrl = selectedServer || window.location.origin;
    handleCopyVoteLink(eventData.event.Events_ID, baseUrl);
  };

  // Copy spotlight link
  const onCopySpotlightLink = () => {
    if (!eventData?.event.Events_ID || !eventData?.event.Verification_Code) return;
    
    const baseUrl = selectedServer || window.location.origin;
    handleCopySpotlightLink(eventData.event.Events_ID, eventData.event.Verification_Code, baseUrl);
  };

  // Download plan
  const onDownloadPlan = async () => {
    if (!eventData?.event.Events_ID) return;
    await handleDownloadPlan(eventData.event.Events_ID);
  };

  // Download Excel file
  const onDownloadExcel = async () => {
    if (!eventData?.event.Events_ID) return;

    try {
      await handleDownloadExcel(eventData.event.Events_ID);
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Failed to download Excel file');
    }
  };

  // Download votes Excel file
  const onDownloadVotes = async () => {
    if (!eventData?.event.Events_ID) return;
    await handleDownloadVotes(eventData.event.Events_ID);
  };

  // Generate group matches
  const onGenerateMatches = async () => {
    if (!eventData?.event.Events_ID) return;

    try {
      setLoading(true);
      const success = await handleGenerateMatches(eventData.event.Events_ID);
      if (success) {
        // Refresh data
        const refreshedData = await eventApi.getEventViewData(eventData.event.Events_ID);
        setEventData(refreshedData);
        toast.success('Group matches generated successfully');
      }
    } catch (error) {
      console.error('Error generating matches:', error);
      toast.error('Failed to generate matches');
    } finally {
      setLoading(false);
    }
  };

  // Register user for event
  const onRegisterUser = async (userId: string) => {
    if (!eventData?.event.Events_ID) return;

    try {
      await registerUserForEvent(eventData.event.Events_ID, userId);
      
      // Refresh data
      const refreshedData = await eventApi.getEventViewData(eventData.event.Events_ID);
      setEventData(refreshedData);
      
      toast.success("User registered successfully!");
    } catch (error) {
      console.error('Error registering user:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  // Helper functions imported from utils

  // Handle group deletion
  const handleDeleteGroup = async (roundNumber: number, groupId: string) => {
    if (!eventData?.event.Events_ID) return;
    
    // Validate groupId
    if (!groupId) {
      toast.error('Invalid group ID. Please refresh the page and try again.', {
        position: "top-right",
        autoClose: 5000
      });
      return;
    }

    // Show confirmation toast with action button
    toast.warning(
      <div>
        <p>Delete this group? This action cannot be undone.</p>
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          <button 
            onClick={async () => {
              toast.dismiss();
              try {
                await eventApi.deleteGroup(eventData.event.Events_ID, groupId);
                toast.success('Group deleted successfully!', {
                  position: "top-right",
                  autoClose: 3000
                });
                
                // Refresh data
                const refreshedData = await eventApi.getEventViewData(eventData.event.Events_ID);
                setEventData(refreshedData);
              } catch (error) {
                console.error('Error deleting group:', error);
                const errorMessage = await getErrorMessage(error);
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 5000
                });
              }
            }}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Delete
          </button>
          <button 
            onClick={() => toast.dismiss()}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        closeOnClick: false,
        draggable: false
      }
    );
  };

  // Remove user from group
  const handleRemoveUserFromGroup = async (roundNumber: number, groupId: string, userId: string) => {
    if (!eventData?.event.Events_ID) return;
    
    // Validate groupId
    if (!groupId) {
      toast.error('Invalid group ID. Please refresh the page and try again.', {
        position: "top-right",
        autoClose: 5000
      });
      return;
    }

    const user = eventData.users.find(u => u.User_ID === userId);
    
    // Show confirmation toast with action button
    toast.info(
      <div>
        <p>Remove {user?.firstname} {user?.lastname} from this group?</p>
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          <button 
            onClick={async () => {
              toast.dismiss();
              try {
                await eventApi.removeUserFromGroup(eventData.event.Events_ID, groupId, userId);
                toast.success('User removed from group successfully!', {
                  position: "top-right",
                  autoClose: 3000
                });
                
                // Refresh data
                const refreshedData = await eventApi.getEventViewData(eventData.event.Events_ID);
                setEventData(refreshedData);
              } catch (error) {
                console.error('Error removing user from group:', error);
                const errorMessage = await getErrorMessage(error);
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 5000
                });
              }
            }}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Remove
          </button>
          <button 
            onClick={() => toast.dismiss()}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        closeOnClick: false,
        draggable: false
      }
    );
  };

  // Add user to group
  const handleAddUserToGroup = async (groupId: string, userId: string) => {
    if (!eventData?.event.Events_ID) return;
    
    // Validate groupId
    if (!groupId) {
      toast.error('Invalid group ID. Please refresh the page and try again.', {
        position: "top-right",
        autoClose: 5000
      });
      return;
    }
    
    // Validate userId
    if (!userId) {
      toast.error('Invalid user ID. Please try again.', {
        position: "top-right",
        autoClose: 5000
      });
      return;
    }

    try {
      await eventApi.addUserToGroup(eventData.event.Events_ID, groupId, userId);
      
      const user = unmatchedUsers.find(u => u.User_ID === userId) || 
                  eventData.users.find(u => u.User_ID === userId);
      
      toast.success(`${user?.firstname} ${user?.lastname} added to group successfully!`, {
        position: "top-right",
        autoClose: 3000
      });
      
      // Refresh data
      const refreshedData = await eventApi.getEventViewData(eventData.event.Events_ID);
      setEventData(refreshedData);
      
      // Refresh unmatched users
      if (selectedRoundForUnmatched !== null) {
        await fetchUnmatchedUsers(selectedRoundForUnmatched);
      }
    } catch (error) {
      console.error('Error adding user to group:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Fetch unmatched users
  const fetchUnmatchedUsers = async (roundNumber?: number) => {
    if (!eventData?.event.Events_ID) return;

    try {
      const response = await eventApi.getUnmatchedUsers(eventData.event.Events_ID, roundNumber);
      setUnmatchedUsers(response.unmatched_users);
      setSelectedRoundForUnmatched(roundNumber || null);
    } catch (error) {
      console.error('Error fetching unmatched users:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Show add users modal
  const handleShowAddUsersModal = async (roundNumber: number, groupId: string) => {
    setSelectedGroupForAddUsers({ roundNumber, groupId });
    await fetchUnmatchedUsers(roundNumber);
    setSelectedUsersToAdd([]);
    setShowAddUsersModal(true);
  };

  // Toggle user selection in add users modal
  const handleToggleUserSelection = (userId: string) => {
    setSelectedUsersToAdd(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Add selected users to group
  const handleAddSelectedUsersToGroup = async () => {
    if (!selectedGroupForAddUsers || selectedUsersToAdd.length === 0) return;

    try {
      // Add each selected user to the group
      for (const userId of selectedUsersToAdd) {
        await eventApi.addUserToGroup(
          eventData!.event.Events_ID, 
          selectedGroupForAddUsers.groupId, 
          userId
        );
      }

      toast.success(`${selectedUsersToAdd.length} user(s) added to group successfully!`, {
        position: "top-right",
        autoClose: 3000
      });

      // Refresh data
      const refreshedData = await eventApi.getEventViewData(eventData!.event.Events_ID);
      setEventData(refreshedData);
      
      // Refresh unmatched users
      await fetchUnmatchedUsers(selectedGroupForAddUsers.roundNumber);
      
      // Close modal and reset state
      setShowAddUsersModal(false);
      setSelectedGroupForAddUsers(null);
      setSelectedUsersToAdd([]);
      
    } catch (error) {
      console.error('Error adding users to group:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Show unmatched users modal
  const handleShowUnmatchedUsers = async (roundNumber?: number) => {
    await fetchUnmatchedUsers(roundNumber);
    setShowUnmatchedUsersModal(true);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, user: User) => {
    setDraggedUser(user);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', user.User_ID);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback (only if not already added)
    const target = e.currentTarget as HTMLElement;
    if (!target.classList.contains(styles.dragOver)) {
      target.classList.add(styles.dragOver);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only remove highlight if we're actually leaving the drop zone
    // (not just moving to a child element)
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    // Check if mouse is still within the target bounds
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      target.classList.remove(styles.dragOver);
    }
  };

  const handleDrop = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const target = e.currentTarget as HTMLElement;
    target.classList.remove(styles.dragOver);
    
    console.log(`🎯 Drop event fired:`, { groupId, draggedUser });
    
    if (draggedUser && groupId) {
      handleAddUserToGroup(groupId, draggedUser.User_ID);
      setDraggedUser(null);
    } else {
      console.warn(`❌ Drop failed:`, { 
        hasDraggedUser: !!draggedUser, 
        groupId,
        draggedUserId: draggedUser?.User_ID 
      });
    }
  };

  // Download user mail
  const handleDownloadUserMail = (userId: string) => {
    if (!eventData?.event.Events_ID) return;
    
    const user = eventData.users.find(u => u.User_ID === userId);
    if (user) {
      const downloadUrl = `/api/events/${eventData.event.Events_ID}/matches/mail/${userId}/`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `matches_${eventData.event.title}_user_${user.mail}.eml`;
      link.click();
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString('de-DE');
    } catch {
      return dateString;
    }
  };

  // State for user registration
  const [registeredUsers, setRegisteredUsers] = useState<User[]>([]);
  const [unregisteredUsers, setUnregisteredUsers] = useState<User[]>([]);
  const [filteredUnregisteredUsers, setFilteredUnregisteredUsers] = useState<User[]>([]);

  // Effect to initialize registered and unregistered users
  useEffect(() => {
    if (eventData) {
      const registered = getRegisteredUsers(eventData);
      setRegisteredUsers(registered);
      
      // Fetch unregistered users
      const fetchUsers = async () => {
        if (eventId) {
          const unregistered = await getUnregisteredUsers(eventId);
          setUnregisteredUsers(unregistered);
          setFilteredUnregisteredUsers(unregistered);
        }
      };
      
      fetchUsers();
    }
  }, [eventData, eventId]);

  // Effect to filter unregistered users based on search input
  useEffect(() => {
    setFilteredUnregisteredUsers(getFilteredUnregisteredUsers(unregisteredUsers, userSearchInput));
  }, [unregisteredUsers, userSearchInput]);

  // Register a user for the event
  const handleRegisterUserForEvent = async (userId: string) => {
    if (!eventId) return;
    
    try {
      await registerUserForEvent(eventId, userId);
      
      // Find the user in all users
      const allUsers = [...(eventData?.users || []), ...unregisteredUsers];
      const user = allUsers.find(u => u.User_ID === userId);
      
      if (user) {
        // Update the UI state
        setRegisteredUsers(prev => [...prev, user]);
        setUnregisteredUsers(prev => prev.filter(u => u.User_ID !== userId));
        
        toast.success(`${user.firstname} ${user.lastname} wurde erfolgreich für das Event registriert.`);
      }
      
      // Close the modal
      setShowRegisterUserModal(false);
    } catch (error) {
      console.error('Error registering user:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Fehler bei der Registrierung: ${errorMessage}`);
    }
  };
  
  // Unregister a user from the event
  const handleUnregisterUserFromEvent = async (userId: string) => {
    if (!eventId) return;
    
    try {
      await unregisterUserFromEvent(eventId, userId);
      
      // Find the user in all users
      const allUsers = [...(eventData?.users || []), ...unregisteredUsers];
      const user = allUsers.find(u => u.User_ID === userId);
      
      if (user) {
        // Update the UI state
        setRegisteredUsers(prev => prev.filter(u => u.User_ID !== userId));
        setUnregisteredUsers(prev => [...prev, user]);
        
        toast.success(`${user.firstname} ${user.lastname} wurde erfolgreich vom Event abgemeldet.`);
      }
    } catch (error) {
      console.error('Error unregistering user:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Fehler bei der Abmeldung: ${errorMessage}`);
    }
  };
  
  // Download user's mail
  const onDownloadUserMail = (userId: string) => {
    handleDownloadUserMail(userId);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading event data...</p>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className={styles.errorContainer}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Event not found</h4>
          <p>The requested event could not be loaded.</p>
          <hr />
          <button className="btn btn-primary" onClick={() => navigate('/events')}>
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  // Use values from state instead of recreating them
  const registrationLink = `${window.location.origin}/register/?event=${eventData.event.Events_ID}&verification_code=${eventData.event.Verification_Code}`;
  const participantsLink = `${window.location.origin}/events/${eventData.event.Events_ID}/participants/`;

  return (
    <div className="container mt-4">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <button onClick={() => navigate('/events')} className="btn btn-link p-0">
              Events
            </button>
          </li>
          <li className="breadcrumb-item">
            <span>{eventData.event.title}</span>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Edit
          </li>
        </ol>
      </nav>

      {/* Role Management Card */}
      <div className="card p-4 role-container shadow-sm">
        <div className="role-header d-flex align-items-center justify-content-between">
          <div>
            <i className="bi bi-calendar3-event"></i>
            <h4 className="mb-0 d-inline"> Event - {eventData.event.title} - Admin</h4>
          </div>
          <div>
            <button 
              className="btn btn-outline-primary mb-3" 
              onClick={() => setShowActionMenu(!showActionMenu)}
            >
              <i className="bi bi-sliders"></i> Show Action Menu
            </button>
          </div>
        </div>

        <hr />
        
        {/* Event Information */}
        <div className="row mb-3">
          <div className="col-md-6">
            <ul className="list-group">
              <li className="list-group-item"><strong>Users with choices:</strong> {eventData.selections_user_count}</li>
              <li className="list-group-item"><strong>Count of votes:</strong> {eventData.votes_count}</li>
              <li className="list-group-item"><strong>Count of Registrations:</strong> <span id="registrationCountDisplay">{eventData.registration_user_count}</span></li>
            </ul>
          </div>
          <div className="col-md-6">
          </div>
        </div>

        <hr />
        
        {/* Improved Expandable Sections */}
        <div className="row mb-3">
          <div className="accordion" id="eventAccordion">
            {/* TimeTable Section - Button to open modal */}
            <div className="accordion-item border-0 shadow-sm mb-3 rounded">
              <h2 className="accordion-header">
                <button 
                  className="accordion-button collapsed bg-warning bg-opacity-10" 
                  type="button" 
                  onClick={() => setShowTimeTableModal(true)}
                >
                  <i className="bi bi-calendar2-week me-2"></i> Time Schedule <span className="ms-2 badge bg-secondary">{timeTableCount}</span>
                </button>
              </h2>
            </div>
            
            {/* Votes Section - View Only */}
            <div className="accordion-item border-0 shadow-sm mb-3 rounded">
              <h2 className="accordion-header">
                <button 
                  className="accordion-button collapsed bg-info bg-opacity-10" 
                  type="button" 
                  onClick={() => setShowVotesModal(true)}
                >
                  <i className="bi bi-check2-circle me-2"></i> Votes <span className="ms-2 badge bg-secondary">{eventData.votes_count}</span>
                </button>
              </h2>
            </div>

            {/* Registration section */}
            <div className="accordion-item border-0 shadow-sm mb-3 rounded">
              <h2 className="accordion-header">
                <button 
                  className="accordion-button collapsed bg-success bg-opacity-10" 
                  type="button" 
                  onClick={() => setShowRegisteredUsersModal(true)}
                >
                  <i className="bi bi-person-check me-2"></i> Registrations <span className="ms-2 badge bg-secondary">{eventData.registration_user_count}</span>
                </button>
              </h2>
            </div>


            {/* Edit section */}
            <div className="accordion-item border-0 shadow-sm mb-3 rounded">
              <h2 className="accordion-header">
                <button 
                  className="accordion-button collapsed bg-primary bg-opacity-10" 
                  type="button" 
                  onClick={() => setShowEditEventModal(true)}
                >
                  <i className="bi bi-person-check me-2"></i> Event Edit 
                </button>
              </h2>
            </div>

          </div>
        </div>
      </div>

      {/* Group Matches Container */}


        <GroupMatches 
          eventData={eventData}
          onGenerateMatches={onGenerateMatches}
          handleShowUnmatchedUsers={handleShowUnmatchedUsers}
          handleDeleteGroup={handleDeleteGroup}
          handleRemoveUserFromGroup={handleRemoveUserFromGroup}
          handleAddUserToGroup={handleAddUserToGroup}
          fetchUnmatchedUsers={fetchUnmatchedUsers}
          handleGenerateMatches={onGenerateMatches}
          handleDownloadExcel={onDownloadExcel}
          handleShowAddUsersModal={handleShowAddUsersModal}
        />
      

      {/* Registered Users Modal */}
      <RegisteredUsersModal
        show={showRegisteredUsersModal}
        onClose={() => setShowRegisteredUsersModal(false)}
        registeredUsers={registeredUsers}
        registrations={eventData?.registrations || []}
        handleDownloadUserMail={onDownloadUserMail}
        setShowRegisterUserModal={() => setShowRegisterUserModal(true)}
        handleUnregisterUser={handleUnregisterUserFromEvent}
        eventId={eventId || ''}
      />

      {/* Register User Modal */}
      <RegisterUserModal
        show={showRegisterUserModal}
        onClose={() => setShowRegisterUserModal(false)}
        userSearchInput={userSearchInput}
        setUserSearchInput={setUserSearchInput}
        filteredUnregisteredUsers={filteredUnregisteredUsers}
        handleRegisterUser={handleRegisterUserForEvent}
      />

      {/* Send Mail Confirmation Modal */}
      <SendMailModal
        show={showSendMailModal}
        onClose={() => setShowSendMailModal(false)}
        handleSendVotingEmail={onSendVotingEmail}
      />

      {/* Email Result Modal */}
      <EmailResultModal
        show={showEmailResultModal}
        onClose={() => setShowEmailResultModal(false)}
        emailResult={emailResult}
      />

      {/* Votes Modal */}
      <VotesModal
        show={showVotesModal}
        onClose={() => setShowVotesModal(false)}
        votes={eventData?.votes || []}
        users={eventData?.users || []}
        handleDownloadVotes={onDownloadVotes}
      />

      {/* Time Table Modal */}
      <TimeTableModal
        show={showTimeTableModal}
        onClose={() => setShowTimeTableModal(false)}
        onTimeTableUpdate={handleTimeTableUpdate}
      />

      {/* Timeline Modal */}
      <TimelineModal
        show={showTimelineModal}
        onClose={() => setShowTimelineModal(false)}
        eventData={eventData}
        formatDate={formatDate}
      />

      {/* Unmatched Users Modal */}
      <UnmatchedUsersModal
        show={showUnmatchedUsersModal}
        onClose={() => setShowUnmatchedUsersModal(false)}
        unmatchedUsers={unmatchedUsers}
        selectedRoundForUnmatched={selectedRoundForUnmatched}
        handleDragStart={handleDragStart}
        handleShowUnmatchedUsers={handleShowUnmatchedUsers}
      />

      {/* Add Users to Group Modal */}
      <AddUsersModal
        show={showAddUsersModal}
        onClose={() => {
          setShowAddUsersModal(false);
          setSelectedGroupForAddUsers(null);
          setSelectedUsersToAdd([]);
        }}
        unmatchedUsers={unmatchedUsers}
        selectedGroupForAddUsers={selectedGroupForAddUsers}
        selectedUsersToAdd={selectedUsersToAdd}
        handleToggleUserSelection={handleToggleUserSelection}
        handleAddSelectedUsersToGroup={handleAddSelectedUsersToGroup}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        show={showEditEventModal}
        onHide={() => setShowEditEventModal(false)}
        event={eventData?.event || null}
        onEventUpdated={(updatedEvent) => {
          if (eventData) {
            setEventData({
              ...eventData,
              event: updatedEvent
            });
          }
        }}
      />

      {/* Action Menu Modal */}
      <ActionMenuModal
        show={showActionMenu}
        onClose={() => setShowActionMenu(false)}
        eventTitle={eventData.event.title || ''}
        selectedServer={selectedServer}
        setSelectedServer={setSelectedServer}
        onCopyRegisterLink={onCopyRegisterLink}
        onCopyVoteLink={onCopyVoteLink}
        onCopySpotlightLink={onCopySpotlightLink}
        onCopyVerificationCode={onCopyVerificationCode}
        onSendVotingEmail={() => setShowSendMailModal(true)}
        onShowTimeline={() => setShowTimelineModal(true)}
      />

          </div>
  );
};

export default EventView;
